/**
 * The PRO column VOCABULARY: each static column id -> the
 * `field_definitions.fieldname`(s) whose data that column renders.
 *
 * Its own module, holding nothing but data, because two very different things read
 * it and one of them must not pull React in:
 *
 *   * proCardColumns, which uses it to keep the picker from offering a field a
 *     static column already shows, and to let that column inherit the registry's
 *     title (re-exported there as PRO_CARD_COVERED_FIELDNAMES, its original name).
 *   * the layout matcher (utils/fieldAliases), as the LAST RESORT for a column id
 *     it is handed with no declared identity.
 *
 * That last reader is why this is global rather than something a host declares.
 * The identity has to be known from the ID ALONE, in both directions: a layout
 * saved on the PRO grid and loaded on an LC bucket card arrives as `available` /
 * `lfd` / `puAppt`, and the LC card has no reason to know those mean
 * `availablegoodtogodatetime` / `lastfreedaydate` / `o_origapptdate` — it names its
 * own columns after the fields, correctly. Nothing derivable connects the two
 * spellings, so without this the layout is refused in that direction until someone
 * happens to re-save it.
 *
 * Why an explicit map instead of matching names: the static column ids are API
 * names (`terminal`, `lfd`, `deliverTo`) while the registry keys off raw CMS
 * fieldnames (`rail`, `lastfreedaydate`, `destname`). A normalized name compare
 * only ever caught the coincidental matches — `movetype`/`container`/`size` and
 * friends — and leaked the other 20+, so "Terminal" and "Last Free Day" both sat
 * in the picker alongside the built-in columns showing the same values. Entries
 * whose id already equals its fieldname are omitted here; the name compare still
 * covers those (and stays as the safety net for any column added later).
 *
 * ONLY list a fieldname whose value is genuinely the same. Deliberately absent:
 *   * billFrom / invDate / billToRef — the static columns take the LATEST invoice
 *     (`ORDER BY inv.recno DESC LIMIT 1`, so an accessorial row can win), NOT the
 *     main invoice the `Main Invoice - …` defs resolve. Measured on prod: they
 *     disagree on 277 / 763 / 3476 of 32,772 current pros, so suppressing those
 *     defs would cost real data.
 *   * `anyinv_*` — "Any Invoice" is a different question from the static column.
 *   * cityStateZip / serviceFailure* — no orders-scope def exists to collide with.
 *   * `d_destappttime1` / `d_destappttime2` — the `delivTimes` cell joins BOTH into
 *     one string, which is right for reading the window at a glance and useless for
 *     sorting, column-filtering (the facets are whole pairs) or exporting one end of
 *     it. Claiming them withheld two fields that every other grid offers separately,
 *     for a column that is not a substitute. The merged cell stays; the two defs stay
 *     addable beside it.
 */
export const PRO_COLUMN_IDENTITIES: Readonly<Record<string, readonly string[]>> = {
  // orderscurrent columns the static select renames or formats.
  terminal: ['rail'],                          // 'Terminal'
  lfd: ['lastfreedaydate'],                    // 'Last Free Day'
  available: ['availablegoodtogodatetime'],    // 'GWI Available/GTG Date'
  outGate: ['outgatedatetime'],                // 'Rail/Port Out-Gate Date'
  perDiem: ['perdiemstartdate'],               // 'Per Diem LFD'
  docCut: ['documentcutoffdatetime'],          // 'Doc Cutoff Date/Time'
  cutoff: ['cutoffdatetime'],                  // 'Cutoff Date/Time'
  traceTerminate: ['traceterminatedatetime'],  // 'Trace Terminate Date'
  ssline: ['scac'],                            // 'SCAC'
  // The dispatcher cell renders `dispatcherName || dispatcher`, i.e. the joined
  // dispatchers row — so both halves of that join are already on screen
  // (dispatchers.username is the join key, so it equals orderscurrent.dispatcher).
  dispatcher: ['dispatchername', 'dispatcherusername'],
  // Leg-derived columns: the static select joins the originate / delivery /
  // terminate legs directly, which is what the oleg_ / dleg_ / tleg_ defs resolve.
  puAppt: ['o_origapptdate'],                  // 'Pickup Appointment'
  pickupFrom: ['origname'],                    // 'Pickup Location'
  delivAppt: ['d_destapptdate'],               // 'Delivery Appointment'
  deliveryDriver: ['d_driver'],                // 'Delivery Driver'
  deliveryDate: ['d_arrivaldatetime'],         // 'Delivery Leg - Dest Arrival'
  deliverTo: ['destname'],                     // 'Delivery Location'
  terminateDate: ['t_arrivaldatetime'],        // 'Terminate Leg - Dest Arrival'
  terminateDriver: ['t_driver'],               // 'Terminate Driver'
  returnTo: ['t_destname'],                    // 'Terminate Destination - Location'
};

